import { Link, useRouterState } from "@tanstack/react-router";
import { motion } from "motion/react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface TabItem {
  /** Path segment (e.g. "clusters", "articles"). Full path becomes `${basePath}/${value}`. */
  value: string;
  /** Label shown in the tab. */
  label: string;
}

export interface TabProps {
  /** List of tabs: value (path segment) and label. */
  tabs: TabItem[];
  /** Base path without trailing slash (e.g. `/discovery/123`). Each tab links to `${basePath}/${value}`. */
  basePath: string;
  /** Optional class name for the tab strip container (nav). */
  className?: string;
  /** Optional class name applied to each tab link. Default tab has transparent background; active gets font-medium + sliding underline. */
  tabClassName?: string;
  /** Accessible label for the nav. */
  "aria-label"?: string;
}

export interface TabPanelProps {
  /** Panel content (e.g. `<Outlet />` when used with a router). */
  children: ReactNode;
  /** Optional class name for the panel container. */
  className?: string;
  /**
   * When provided, panel content animates on change (fade + slide in).
   * Pass the active tab key (e.g. pathname or tab value). Only enter animation is used
   * so content is not shown twice during transition (Outlet always renders current route).
   */
  animationKey?: string;
}

/** Short, opacity-only to avoid choppy animation when panel contains heavy content (e.g. AG Grid). */
const panelTransition = {
  type: "tween" as const,
  duration: 0.12,
  ease: [0.25, 0.1, 0.25, 1] as const,
};

/**
 * Tab strip built from router links. Active state is derived from the current route.
 * Uses a sliding violet underline. Indicator position is updated in useEffect (after paint)
 * to avoid synchronous layout reads that can contribute to choppy tab switches.
 */
const Tab = ({
  tabs,
  basePath,
  className,
  tabClassName,
  "aria-label": ariaLabel = "Tabs",
}: TabProps) => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  const activeIndex = tabs.findIndex(
    (tab) =>
      pathname === `${basePath}/${tab.value}` ||
      pathname === `${basePath}/${tab.value}/`,
  );

  // Defer layout read to after paint so it doesn't block; reduces choppiness when switching tabs.
  useEffect(() => {
    if (activeIndex < 0 || !navRef.current) return;
    const el = tabRefs.current[activeIndex];
    if (!el) return;
    const nav = navRef.current.getBoundingClientRect();
    const tab = el.getBoundingClientRect();
    setIndicator({
      left: tab.left - nav.left,
      width: tab.width,
    });
  }, [activeIndex, pathname, tabs.length]);

  return (
    <nav
      ref={navRef}
      aria-label={ariaLabel}
      className={cn("relative flex gap-6", className)}
    >
      {tabs.map((tab, index) => {
        const href = `${basePath}/${tab.value}`;
        const isActive = pathname === href || pathname === `${href}/`;

        return (
          <Link
            key={tab.value}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            to={href}
            className={cn(
              "bg-transparent px-0 py-3 text-sm no-underline outline-hidden cursor-pointer",
              "transition-colors duration-150 ease-out",
              "focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2",
              "hover:text-gray-700",
              isActive && "font-medium",
              tabClassName,
            )}
            aria-current={isActive ? "page" : undefined}
          >
            {tab.label}
          </Link>
        );
      })}
      {activeIndex >= 0 && (
        <motion.span
          className="absolute bottom-0 h-0.5 bg-violet-800"
          aria-hidden
          animate={{ left: indicator.left, width: indicator.width }}
          transition={{
            type: "tween",
            duration: 0.15,
            ease: [0.25, 0.1, 0.25, 1],
          }}
          initial={false}
        />
      )}
    </nav>
  );
};

/**
 * Wrapper for tab panel content. Uses flex-1 min-h-0 so it fills remaining
 * space in a flex column layout; overflow-hidden so children can manage scroll.
 * Use with TanStack Router by wrapping `<Outlet />`.
 * Pass animationKey (e.g. pathname) to animate content when the tab changes.
 */
export const TabPanel = ({
  children,
  className,
  animationKey,
}: TabPanelProps) => (
  <div
    className={cn("min-h-0 flex-1 overflow-hidden", className)}
    role="tabpanel"
  >
    {animationKey !== undefined ? (
      <motion.div
        key={animationKey}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={panelTransition}
        className="flex h-full min-h-0 flex-col"
      >
        {children}
      </motion.div>
    ) : (
      <div className="flex h-full min-h-0 flex-col">{children}</div>
    )}
  </div>
);

export default Tab;
